import { type FormEvent, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import { isMarketingOnlyDomain, MarketingFooter, MarketingHeader } from '@/modules/marketing/components/site-chrome';
import { ProductSlider } from '@/modules/marketing/components/product-slider';
import { PRICINGCRM_FEATURES } from '@/modules/marketing/components/pricingcrm-content';
import { PRODUCTS } from '@/modules/marketing/components/product-visuals';
import { useSubmitDemoRequest, usePublicIndustries } from '@/modules/marketing/hooks/use-demo-request';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

const APPLICATION_OPTIONS = PRODUCTS.map((p) => ({ value: p.title, label: p.title }));
const PRICINGCRM_APPLICATION_OPTIONS = PRICINGCRM_FEATURES.map((f) => ({ value: f.title, label: f.title }));

function DemoRequestForm() {
    const { data: industries } = usePublicIndustries();
    const submit = useSubmitDemoRequest();
    const applicationOptions = isMarketingOnlyDomain() ? PRICINGCRM_APPLICATION_OPTIONS : APPLICATION_OPTIONS;

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [application, setApplication] = useState('');
    const [industry, setIndustry] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [message, setMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const industryOptions = (industries ?? []).map((i) => ({ value: i.name, label: i.name }));

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setErrorMessage(null);

        try {
            const result = await submit.mutateAsync({
                name,
                email,
                phone,
                application: application || undefined,
                industry: industry || undefined,
                address: address || undefined,
                city: city || undefined,
                zip_code: zipCode || undefined,
                message: message || undefined,
            });
            setSuccessMessage(result.message);
        } catch {
            const supportEmail = isMarketingOnlyDomain() ? 'hello@pricingcrm.com' : 'hello@aipa.io';
            setErrorMessage(`Something went wrong submitting your request — please try again, or email us at ${supportEmail}.`);
        }
    }

    if (successMessage) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border bg-card p-10 text-center shadow-xl">
                <CheckCircle2 className="size-12 text-emerald-500" />
                <h3 className="text-xl font-bold tracking-tight">You&rsquo;re all set</h3>
                <p className="max-w-sm text-sm text-muted-foreground">{successMessage}</p>
            </div>
        );
    }

    return (
        <form className="space-y-4 rounded-2xl border bg-card p-6 shadow-xl sm:p-8" onSubmit={handleSubmit}>
            <div>
                <h2 className="text-xl font-bold tracking-tight">Book a demo</h2>
                <p className="mt-1 text-sm text-muted-foreground">Tell us a bit about you — we&rsquo;ll take it from there.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="dr-name">Full name</Label>
                    <Input id="dr-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Taylor" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="dr-email">Work email</Label>
                    <Input
                        id="dr-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="jordan@company.com"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="dr-phone">Phone number</Label>
                    <Input
                        id="dr-phone"
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(555) 123-4567"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Application you&rsquo;d like to see</Label>
                    <SearchableSelect
                        value={application}
                        onValueChange={setApplication}
                        options={applicationOptions}
                        placeholder="Select an application…"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label>Industry</Label>
                <SearchableSelect
                    value={industry}
                    onValueChange={setIndustry}
                    options={industryOptions}
                    placeholder="Select your industry…"
                />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr_1fr]">
                <div className="space-y-2">
                    <Label htmlFor="dr-address">Address</Label>
                    <Input id="dr-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Optional" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="dr-city">City</Label>
                    <Input id="dr-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Optional" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="dr-zip">ZIP</Label>
                    <Input id="dr-zip" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="Optional" />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="dr-message">What would you like to see? (optional)</Label>
                <Textarea
                    id="dr-message"
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us about your team or what you're hoping to solve…"
                />
            </div>

            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

            <Button type="submit" size="lg" className="w-full" loading={submit.isPending}>
                Request my demo
            </Button>
            <p className="text-center text-[11px] text-muted-foreground">
                We&rsquo;ll send a confirmation to your email and phone right away.
            </p>
        </form>
    );
}

export function BookADemoPage() {
    const pricingCrm = isMarketingOnlyDomain();

    usePageSeo(
        pricingCrm
            ? {
                  title: 'Book a Demo — See PricingCRM in Action',
                  description:
                      'Schedule a personalized demo of PricingCRM’s Lead Generator, Pricing Management, and CMA reporting tools — see how your team can capture more leads and price with confidence.',
                  keywords: 'book a demo, PricingCRM demo, schedule pricing software demo, pricing intelligence demo, request a demo',
              }
            : {
                  title: 'Book a Demo — See AIPA in Action',
                  description:
                      "Schedule a personalized demo of AIPA's AI Assistant, Pricing Intelligence, and CRM — see how your team can capture and convert more leads automatically.",
                  keywords: 'book a demo, AIPA demo, schedule AI chatbot demo, pricing intelligence demo, request a demo',
              },
    );

    return (
        <div className="min-h-screen bg-background text-foreground">
            <MarketingHeader />

            <section className="relative overflow-hidden">
                <div className="absolute inset-0 -z-10 overflow-hidden">
                    <div className="absolute -left-24 -top-24 size-96 rounded-full bg-primary/10 blur-3xl" />
                    <div className="absolute -bottom-24 -right-16 size-96 rounded-full bg-sky-400/10 blur-3xl" />
                </div>

                <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
                    <h1 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
                        See {pricingCrm ? 'PricingCRM' : 'AIPA'} in action for your business
                    </h1>

                    <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 items-start gap-8 lg:grid-cols-2">
                        <ProductSlider />
                        <DemoRequestForm />
                    </div>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
